import {
  CalendarToday as CalendarTodayIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  DateRange as DateRangeIcon,
  FilterAlt as FilterAltIcon,
  FilterList as FilterIcon,
  FilterListOff as FilterListOffIcon,
  Refresh as RefreshIcon,
  Today as TodayIcon,
  ViewWeek as ViewWeekIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReusableTable from '../../common/table/CommonTable';
import { fetchAllReportList } from '../../redux/admissions/admissionsActions';

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getDate() === d2.getDate() && 
         d1.getMonth() === d2.getMonth() && 
         d1.getFullYear() === d2.getFullYear();
};

const isWithinDateRange = (date, startDate, endDate) => {
  const d = new Date(date);
  const start = new Date(startDate);
  const end = new Date(endDate);
  return d >= start && d <= end;
};

const getStartOfWeek = () => {
  const date = new Date();
  const day = date.getDay();
  const diff = date.getDate() - day;
  return new Date(date.setDate(diff));
};

const getEndOfWeek = () => {
  const date = new Date();
  const day = date.getDay();
  const diff = date.getDate() + (6 - day);
  return new Date(date.setDate(diff));
};

const getStartOfMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const getEndOfMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const getStartOfYear = () => {
  const date = new Date();
  return new Date(date.getFullYear(), 0, 1);
};

const getEndOfYear = () => {
  const date = new Date();
  return new Date(date.getFullYear(), 11, 31);
};

const TIME_PERIODS = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
  CUSTOM: 'custom',
};

const TAB_COLORS = {
  day: '#5e35b1', 
  week: '#00897b',
  month: '#d81b60', 
  year: '#1565c0', 
  custom: '#e65100', 
};

const columns = [
  { id: 'serialNo', label: 'S.No', numeric: true },
  { id: 'patientName', label: 'Patient Name' },
  { 
    id: 'admissionDate', 
    label: 'Admission Date',
    render: (value) => value ? formatDate(new Date(value)) : 'N/A'
  },
  { 
    id: 'dischargeDate', 
    label: 'Discharge Date',
    render: (value) => value ? formatDate(new Date(value)) : 'Pending'
  },
  { id: 'attenderName1', label: 'Attender Name 1' },
  { id: 'attenderName2', label: 'Attender Name 2' },
  { id: 'patientPhone', label: 'Patient Phone' },
  { id: 'attenderPhone1', label: 'Attender Phone 1' },
  { id: 'attenderPhone2', label: 'Attender Phone 2' },
  { id: 'address', label: 'Address' },
  { id: 'wardName', label: 'Ward' },
  { id: 'blockName', label: 'Block' },
  { 
    id: 'patientStatus', 
    label: 'Patient Status',
    render: (value) => (
      <Chip 
        label={value} 
        color={value === 'Admitted' ? 'primary' : 'default'} 
        size="small" 
        sx={{
          fontWeight: 'bold',
          bgcolor: value === 'Admitted' ? '#5e35b1' : '#9575cd',
          color: 'white'
        }}
      />
    )
  },
  { 
    id: 'attenderStatus', 
    label: 'Attender Status',
    render: (value) => (
      <Chip 
        label={value} 
        size="small" 
        sx={{
          fontWeight: 'bold',
          bgcolor: value === 'Active' ? '#00897b' : '#e53935',
          color: 'white'
        }}
      />
    )
  },
];

const DailyLogReport = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState(TIME_PERIODS.DAY);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const reportData = useSelector((state) => state?.admission?.fetchReport || {});
  const admissions = reportData?.admissions?.data || [];

  const totalPages = reportData?.admissions?.totalPages || 0;

  const getCurrentColor = () => TAB_COLORS[timePeriod] || TAB_COLORS.day;
  const fetchData = async (page) => {
    setLoading(true);
    await dispatch(fetchAllReportList(page));
  
  };
  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, dispatch]);
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };


  useEffect(() => {
    if (reportData.success && admissions.length > 0) {
      const processedData = admissions.map((admission, index) => {
        const patient = admission.Patient || {};
        const ward = admission.Ward || {};
        const block = admission.Block || {};
        const attenders = admission.Attenders || [];
        const attender1 = attenders[0] || {};
        const attender2 = attenders[1] || {};
        
        return {
          serialNo: admission.admission_no,
          id: admission.id,
          patientName: patient.patient_name || 'N/A',
          admissionDate: admission.admit_date,
          dischargeDate: admission.discharge_date,
          attenderName1: attender1.attender_name || 'N/A',
          attenderName2: attender2.attender_name || '',
          patientPhone: patient.phone || 'N/A',
          attenderPhone1: attender1.phone || 'N/A',
          attenderPhone2: attender2.phone || '',
          address: (attender1.address || patient.address || 'N/A'),
          wardName: ward.ward_name || 'N/A',
          blockName: block.block_name || 'N/A',
          patientStatus: admission.discharge_date ? 'Discharged' : 'Admitted',
          attenderStatus: (attenders.length > 0 && attenders[0].attender_status === 1) ? 'Active' : 'Inactive'
        };
      });
      
      setPatients(processedData);
      setLoading(false);
    }
  }, [reportData, admissions]);


  useEffect(() => {
    let start = new Date();
    let end = new Date();

    switch (timePeriod) {
      case TIME_PERIODS.DAY:
        break;
      case TIME_PERIODS.WEEK:
        start = getStartOfWeek();
        end = getEndOfWeek();
        break;
      case TIME_PERIODS.MONTH:
        start = getStartOfMonth();
        end = getEndOfMonth();
        break;
      case TIME_PERIODS.YEAR:
        start = getStartOfYear();
        end = getEndOfYear();
        break;
      case TIME_PERIODS.CUSTOM:
        start = startDate;
        end = endDate;
        break;
      default:
        break;
    }

    setStartDate(start);
    setEndDate(end);

    // Apply filters once patients data is available
    if (patients.length > 0) {
      applyFilters(start, end, searchText, statusFilter);
    }
  }, [timePeriod, patients]);

  // Apply filters based on custom date range
  useEffect(() => {
    if (timePeriod === TIME_PERIODS.CUSTOM && patients.length > 0) {
      applyFilters(startDate, endDate, searchText, statusFilter);
    }
  }, [startDate, endDate]);

  // Apply filters based on search text and status filter
  useEffect(() => {
    if (patients.length > 0) {
      applyFilters(startDate, endDate, searchText, statusFilter);
    }
  }, [searchText, statusFilter]);

  const applyFilters = (start, end, search, status) => {
    let filtered = [...patients];
    
    // Filter by date range
    filtered = filtered.filter(patient => {
      const admissionDate = new Date(patient.admissionDate);
      return isWithinDateRange(admissionDate, start, end) || 
             isSameDay(admissionDate, start) || 
             isSameDay(admissionDate, end);
    });

    
    // Filter by search text
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        patient =>
          (patient.patientName && patient.patientName.toLowerCase().includes(searchLower)) ||
          (patient.attenderName1 && patient.attenderName1.toLowerCase().includes(searchLower)) ||
          (patient.attenderName2 && patient.attenderName2.toLowerCase().includes(searchLower)) ||
          (patient.patientPhone && patient.patientPhone.includes(search)) ||
          (patient.attenderPhone1 && patient.attenderPhone1.includes(search)) ||
          (patient.attenderPhone2 && patient.attenderPhone2.includes(search)) ||
          (patient.address && patient.address.toLowerCase().includes(searchLower)) ||
          (patient.wardName && patient.wardName.toLowerCase().includes(searchLower)) ||
          (patient.blockName && patient.blockName.toLowerCase().includes(searchLower))
      );
    }
    
    // Filter by patient status
    if (status !== 'all') {
      filtered = filtered.filter(patient => patient.patientStatus === status);
    }

    setFilteredPatients(filtered);
  };

  const handleTimePeriodChange = (event, newValue) => {
    setTimePeriod(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleDateChange = (event, isStart) => {
    const newDate = new Date(event.target.value);
    if (isStart) {
      setStartDate(newDate);
    } else {
      setEndDate(newDate);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    dispatch(fetchAllReportList()).then(() => {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    });
  };

  const dateToInputValue = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 2, 
        mb: 4, 
        background: 'linear-gradient(to right bottom, #ffffff, #f9f9ff)',
        borderRadius: '12px',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box 
        sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '8px', 
          background: `linear-gradient(to right, ${TAB_COLORS.day}, ${TAB_COLORS.week}, ${TAB_COLORS.month}, ${TAB_COLORS.year}, ${TAB_COLORS.custom})`,
        }} 
      />
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={1}>
        <Typography 
          variant="h4" 
          fontWeight="bold"
          sx={{ 
            color: getCurrentColor(),
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '6px',
              height: '34px',
              backgroundColor: getCurrentColor(),
              marginRight: '12px',
              borderRadius: '3px',
            }
          }}
        >
          Patient Reports
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={handleRefresh} 
              sx={{ 
                color: getCurrentColor(),
                '&:hover': { 
                  backgroundColor: `${getCurrentColor()}22` 
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
            <IconButton 
              onClick={() => setShowFilters(!showFilters)} 
              sx={{ 
                color: getCurrentColor(),
                '&:hover': { 
                  backgroundColor: `${getCurrentColor()}22` 
                },
              }}
            >
              {showFilters ? <FilterListOffIcon /> : <FilterAltIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: '12px', 
          overflow: 'hidden',
          mb: 3, 
          background: '#f5f5f5',
        }}
      >
        <Tabs
          value={timePeriod}
          onChange={handleTimePeriodChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          TabIndicatorProps={{
            style: {
              backgroundColor: getCurrentColor(),
              height: '3px'
            }
          }}
        >
          <Tab 
            icon={<TodayIcon />} 
            label="Today" 
            value={TIME_PERIODS.DAY} 
            iconPosition="start"
            sx={{ 
              color: timePeriod === TIME_PERIODS.DAY ? TAB_COLORS.day : 'text.secondary',
              fontWeight: timePeriod === TIME_PERIODS.DAY ? 'bold' : 'normal',
              '&.Mui-selected': {
                color: TAB_COLORS.day,
              }
            }}
          />
          <Tab 
            icon={<ViewWeekIcon />} 
            label="This Week" 
            value={TIME_PERIODS.WEEK} 
            iconPosition="start"
            sx={{ 
              color: timePeriod === TIME_PERIODS.WEEK ? TAB_COLORS.week : 'text.secondary',
              fontWeight: timePeriod === TIME_PERIODS.WEEK ? 'bold' : 'normal',
              '&.Mui-selected': {
                color: TAB_COLORS.week,
              }
            }}
          />
          <Tab 
            icon={<CalendarViewMonthIcon />} 
            label="This Month" 
            value={TIME_PERIODS.MONTH} 
            iconPosition="start"
            sx={{ 
              color: timePeriod === TIME_PERIODS.MONTH ? TAB_COLORS.month : 'text.secondary', 
              fontWeight: timePeriod === TIME_PERIODS.MONTH ? 'bold' : 'normal',
              '&.Mui-selected': {
                color: TAB_COLORS.month,
              }
            }}
          />
          <Tab 
            icon={<CalendarTodayIcon />} 
            label="This Year" 
            value={TIME_PERIODS.YEAR} 
            iconPosition="start"
            sx={{ 
              color: timePeriod === TIME_PERIODS.YEAR ? TAB_COLORS.year : 'text.secondary',
              fontWeight: timePeriod === TIME_PERIODS.YEAR ? 'bold' : 'normal',
              '&.Mui-selected': {
                color: TAB_COLORS.year,
              }
            }}
          />
          <Tab 
            icon={<DateRangeIcon />} 
            label="Custom Range" 
            value={TIME_PERIODS.CUSTOM} 
            iconPosition="start"
            sx={{ 
              color: timePeriod === TIME_PERIODS.CUSTOM ? TAB_COLORS.custom : 'text.secondary',
              fontWeight: timePeriod === TIME_PERIODS.CUSTOM ? 'bold' : 'normal',
              '&.Mui-selected': {
                color: TAB_COLORS.custom,
              }
            }}
          />
        </Tabs>
      </Paper>
      <Box mb={3} display="flex" justifyContent="center" alignItems="center">
        <Chip 
          label={`${formatDate(startDate)} - ${formatDate(endDate)}`}
          sx={{ 
            color: '#fff',
            backgroundColor: getCurrentColor(),
            fontWeight: 'bold',
            px: 2,
            py: 2.5,
            '& .MuiChip-icon': {
              color: '#fff'
            }
          }}
          icon={<DateRangeIcon style={{ color: '#fff' }} />}
        />
      </Box>
      {timePeriod === TIME_PERIODS.CUSTOM && (
        <Paper
          elevation={0}
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '12px',
            border: `1px solid ${getCurrentColor()}33`,
            background: `${getCurrentColor()}08`,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                value={dateToInputValue(startDate)}
                onChange={(e) => handleDateChange(e, true)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: getCurrentColor(),
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: getCurrentColor(),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                type="date"
                value={dateToInputValue(endDate)}
                onChange={(e) => handleDateChange(e, false)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: getCurrentColor(),
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: getCurrentColor(),
                  },
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}
      {showFilters && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '12px',
            background: 'linear-gradient(to right bottom, #f5f5f5, #e8eaf6)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              color: getCurrentColor(),
              fontWeight: 'bold',
            }}
          >
            <FilterIcon sx={{ mr: 1, verticalAlign: 'middle', color: getCurrentColor() }} />
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Search"
                value={searchText}
                onChange={handleSearchChange}
                placeholder="Search by name, phone, ward, block..."
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: getCurrentColor(),
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: getCurrentColor(),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel 
                  id="status-filter-label"
                  sx={{
                    '&.Mui-focused': {
                      color: getCurrentColor(),
                    },
                  }}
                >Patient Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Patient Status"
                  onChange={handleStatusFilterChange}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      '&.Mui-focused': {
                        borderColor: getCurrentColor(),
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="Admitted">Admitted</MenuItem>
                  <MenuItem value="Discharged">Discharged</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
        }}
      >
        <Box padding={3}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 'bold',
              color: getCurrentColor(),
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                display: 'inline-block',
                width: '4px',
                height: '24px',
                backgroundColor: getCurrentColor(),
                marginRight: '10px',
                borderRadius: '2px',
              }
            }}
          >
            Patient Log: {formatDate(startDate)} - {formatDate(endDate)}
          </Typography>
        </Box>

        <ReusableTable
          columns={columns}
          data={filteredPatients}
          isLoading={loading}
          title="Patient Report"
          headerColor="#000"
          rowColor="#ffffff"
          alternateRowColor="#f5f5f5"
          withPagination={true}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          showDownloadOption={true}
          withActions={false}
          customEmptyMessage="No patient records found for the selected criteria"
        />
      </Paper>
    </Paper>
  );
};

export default DailyLogReport;